import type { ReadingSlide } from "../ui/StartReader";
import { Frame, C, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the ransomware-office start screen. Three
 * short slides (what/who, how the attack gets in, key terms) shown in the
 * reader while the 3D room downloads. Content only; the reader UI lives in
 * ui/StartReader.tsx and the shared art toolkit in ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* Shared: a padlock glyph, drawn around (0,0)                         */
/* ------------------------------------------------------------------ */
function Padlock({ color, bodyFill }: { color: string; bodyFill: string }): JSX.Element {
  return (
    <g>
      <path d="M-6 -2 V-7 a6 6 0 0 1 12 0 V-2" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
      <rect x="-10" y="-3" width="20" height="15" rx="3" fill={color} />
      <circle cx="0" cy="3.5" r="2.2" fill={bodyFill} />
      <rect x="-0.8" y="4" width="1.6" height="4.5" rx="0.8" fill={bodyFill} />
    </g>
  );
}

/* ------------------------------------------------------------------ */
/* 1. Why it matters: the lock screen a victim sees                    */
/* ------------------------------------------------------------------ */
function LockedFileRow({ y, name }: { y: number; name: string }): JSX.Element {
  return (
    <g>
      <rect x="28.5" y={y + 0.5} width="303" height="21" rx="6" fill={C.panel2} stroke={C.line} />
      <rect x="37" y={y + 5} width="9" height="12" rx="1.6" fill="none" stroke={C.muted} strokeWidth="1.2" />
      <text x="55" y={y + 15} fontSize="11" fontFamily={MONO} fill={C.soft}>
        {name}
      </text>
      <rect x="276" y={y + 4} width="46" height="14" rx="7" fill={C.red} fillOpacity="0.14" />
      <text x="299" y={y + 14.2} fontSize="9" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.5">
        LOCKED
      </text>
    </g>
  );
}

export function RansomLockScreenArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: what the screen shows" aria="A locked screen that says your files have been encrypted, with a pulsing padlock, three locked files and a countdown timer" height={H}>
      <rect x="16.5" y="36.5" width="327" height="244" rx="8" fill={C.code} stroke={C.red} strokeOpacity="0.45" />
      <rect x="17" y="37" width="326" height="26" rx="7" fill={C.red} fillOpacity="0.16" />
      <text x="180" y="54" fontSize="11" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.5">
        YOUR FILES HAVE BEEN ENCRYPTED
      </text>

      {/* pulsing padlock */}
      <circle className="sr-anim-ring" cx="64" cy="105" r="24" fill="none" stroke={C.red} strokeWidth="1.5" />
      <g className="sr-anim-pulse">
        <circle cx="64" cy="105" r="26" fill={C.red} fillOpacity="0.12" />
        <g transform="translate(64 104)">
          <Padlock color={C.red} bodyFill={C.code} />
        </g>
      </g>

      <text x="104" y="92" fontSize="11.5" fontWeight="600" fill={C.text}>
        Every file here is now locked.
      </text>
      <text x="104" y="108" fontSize="11" fill={C.muted}>
        Your backups were deleted first.
      </text>
      <text x="104" y="124" fontSize="11" fill={C.muted}>
        Pay within 48 hours or lose it all.
      </text>

      <LockedFileRow y={146} name="Q3_financials.xlsx" />
      <LockedFileRow y={172} name="client_database.bak" />
      <LockedFileRow y={198} name="payroll_2026.xlsx" />

      <rect x="28.5" y="232.5" width="303" height="36" rx="8" fill={C.amber} fillOpacity="0.08" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-blink" cx="43" cy="250.5" r="3.4" fill={C.red} />
      <text x="54" y="254" fontSize="11" fill={C.muted}>
        Time left
      </text>
      <text x="320" y="256" fontSize="17" fontWeight="700" fontFamily={MONO} fill={C.amber} textAnchor="end">
        47:58:12
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Main risks: timeline of how it gets in                           */
/* ------------------------------------------------------------------ */
type StepGlyph = "phone" | "mail" | "run" | "lock";

function StepGlyphShape({ g, color }: { g: StepGlyph; color: string }): JSX.Element {
  const s = { fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "phone":
      return (
        <g {...s}>
          <rect x="-3.8" y="-6.5" width="7.6" height="13" rx="1.8" />
          <line x1="-1" y1="4.2" x2="1" y2="4.2" />
        </g>
      );
    case "mail":
      return (
        <g {...s}>
          <rect x="-6.5" y="-4.8" width="13" height="9.6" rx="1.6" />
          <polyline points="-6.5,-4 0,1 6.5,-4" />
        </g>
      );
    case "run":
      return (
        <g {...s}>
          <polygon points="-3,-5.5 5.5,0 -3,5.5" />
        </g>
      );
    case "lock":
      return (
        <g {...s}>
          <rect x="-5.5" y="-1" width="11" height="7.5" rx="1.8" />
          <path d="M-3 -1 V-3.6 a3 3 0 0 1 6 0 V-1" />
        </g>
      );
  }
}

interface StepSpec {
  g: StepGlyph;
  title: string;
  sub: string;
  color: string;
}

const STEPS: StepSpec[] = [
  { g: "phone", title: "A call from \u201cIT\u201d", sub: "Friendly, urgent, number looks internal", color: C.amber },
  { g: "mail", title: "An \u201cupdate\u201d arrives by email", sub: "update.exe attached, \u201cfix it today\u201d", color: C.amber },
  { g: "run", title: "You run the file", sub: "One double-click starts the lock-up", color: C.red },
  { g: "lock", title: "Files locked, data stolen", sub: "Ransom note, leak threat, bad news", color: C.red },
];

export function IntrusionTimelineArt(): JSX.Element {
  const H = 296;
  const y0 = 58;
  const pitch = 47;
  return (
    <Frame label="How one call becomes an attack" aria="A four-step timeline: a call from fake IT, an update emailed as an executable, the file is run, and files are locked and data stolen. A note says to break the chain at step one by calling IT back." height={H}>
      <line x1="34" y1={y0} x2="34" y2={y0 + pitch * 3} stroke={C.line} strokeWidth="2" strokeDasharray="3 4" />
      {STEPS.map((s, i) => {
        const cy = y0 + i * pitch;
        return (
          <g key={s.g}>
            {i === 0 && <circle className="sr-anim-ring" cx="34" cy={cy} r="14" fill="none" stroke={C.mint} strokeWidth="1.6" />}
            <g className={i === 3 ? "sr-anim-pulse" : undefined}>
              <circle cx="34" cy={cy} r="15" fill={C.panel2} stroke={s.color} strokeOpacity="0.7" />
              <g transform={`translate(34 ${cy})`}>
                <StepGlyphShape g={s.g} color={s.color} />
              </g>
            </g>
            <text x="60" y={cy - 2} fontSize="12.5" fontWeight="700" fill={C.text}>
              {s.title}
            </text>
            <text x="60" y={cy + 13} fontSize="10.5" fill={C.muted}>
              {s.sub}
            </text>
            <text x="344" y={cy - 2} fontSize="10" fontWeight="700" fill={s.color} textAnchor="end" letterSpacing="0.6">
              {`STEP ${i + 1}`}
            </text>
          </g>
        );
      })}

      <rect x="16.5" y="226.5" width="327" height="56" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.45" />
      <rect x="16" y="232" width="3" height="45" rx="1.5" fill={C.mint} />
      <text x="32" y="246" fontSize="10" fontWeight="700" fill={C.mint} letterSpacing="0.8">
        YOU ARE THE CONTROL
      </text>
      <text x="32" y="262" fontSize="11.5" fill={C.text}>
        Break the chain at step 1: hang up and call
      </text>
      <text x="32" y="277" fontSize="11.5" fill={C.text}>
        IT back on the official number.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Key terms: icon rows                                             */
/* ------------------------------------------------------------------ */
type TermGlyph = "ransom" | "double" | "vish" | "exe" | "backup" | "report";

function TermGlyphShape({ g }: { g: TermGlyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "ransom":
      return (
        <g {...s}>
          <rect x="-5.5" y="-1" width="11" height="7.5" rx="1.8" />
          <path d="M-3 -1 V-3.6 a3 3 0 0 1 6 0 V-1" />
        </g>
      );
    case "double":
      return (
        <g {...s}>
          <path d="M-6 -7 h8 l4 4 v10 h-12 z" />
          <polyline points="2,-7 2,-3 6,-3" />
          <line x1="-2" y1="2" x2="8" y2="2" />
          <polyline points="5,-1 8,2 5,5" />
        </g>
      );
    case "vish":
      return (
        <g {...s}>
          <rect x="-3.8" y="-6.5" width="7.6" height="13" rx="1.8" />
          <line x1="-1" y1="4.2" x2="1" y2="4.2" />
        </g>
      );
    case "exe":
      return (
        <g {...s}>
          <rect x="-8" y="-6" width="16" height="12" rx="2" />
          <polyline points="-4.5,-2 -1.5,0 -4.5,2" />
          <line x1="1" y1="2.5" x2="4.5" y2="2.5" />
        </g>
      );
    case "backup":
      return (
        <g {...s}>
          <ellipse cx="0" cy="-5" rx="6.5" ry="2.6" />
          <path d="M-6.5 -5 V5 a6.5 2.6 0 0 0 13 0 V-5" />
          <path d="M-6.5 0 a6.5 2.6 0 0 0 13 0" />
        </g>
      );
    case "report":
      return (
        <g {...s}>
          <line x1="-5" y1="-7" x2="-5" y2="7" />
          <path d="M-5 -7 h10 l-2.5 3.5 l2.5 3.5 h-10" />
        </g>
      );
  }
}

interface TermSpec {
  g: TermGlyph;
  title: string;
  sub: string;
}

const TERMS: TermSpec[] = [
  { g: "ransom", title: "Ransomware", sub: "Locks your files until someone pays" },
  { g: "double", title: "Double extortion", sub: "Steal the data first, then lock it" },
  { g: "vish", title: "Voice phishing", sub: "A fraud attempt made by phone call" },
  { g: "exe", title: "Executable", sub: "A file that runs code, such as .exe" },
  { g: "backup", title: "Offline backup", sub: "A spare copy attackers can\u2019t reach" },
  { g: "report", title: "Incident report", sub: "Tell security, fast, even if unsure" },
];

export function TermsArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Terms at a glance" aria="Six terms shown as labelled icon rows: ransomware, double extortion, voice phishing, executable, offline backup and incident report" height={H}>
      {TERMS.map((t, i) => {
        const y = 36 + i * 42;
        const last = i === TERMS.length - 1;
        return (
          <g key={t.g} transform={`translate(16 ${y})`}>
            <rect x="0.5" y="0.5" width="327" height="35" rx="9" fill={C.panel2} stroke={C.line} />
            {last && <circle className="sr-anim-ring" cx="24" cy="18" r="13" fill="none" stroke={C.mint} strokeWidth="1.5" />}
            <g className={t.g === "backup" ? "sr-anim-pulse" : undefined}>
              <circle cx="24" cy="18" r="13.5" fill={C.mint} fillOpacity="0.12" />
              <g transform="translate(24 18)">
                <TermGlyphShape g={t.g} />
              </g>
            </g>
            <text x="48" y="15" fontSize="12.5" fontWeight="700" fill={C.text}>
              {t.title}
            </text>
            <text x="48" y="28.5" fontSize="10.5" fill={C.muted}>
              {t.sub}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* Slides                                                              */
/* ------------------------------------------------------------------ */
export const RANSOMWARE_OFFICE_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "One phone call can lock a whole bank.",
    illustration: RansomLockScreenArt,
    caption: "It starts with a request. You decide whether to trust it.",
    sections: [
      {
        h: "What this is about",
        body: "A normal morning at a bank desk. A caller says they are from the IT service desk and need you to install an urgent update. You decide what to believe, what to open, and what to run \u2014 and you see where each choice leads.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who has a work phone, an inbox or a PC. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Filters and antivirus miss some attacks, especially ones that begin with a real person on the phone. The attacker only needs one employee to run one file. Whether that request is genuine is a human call. Yours.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "How the attack gets in, and where to stop it.",
    illustration: IntrusionTimelineArt,
    caption: "Every step is a chance to stop it. Step 1 is the cheapest.",
    sections: [
      {
        h: "A caller you can\u2019t see",
        body: "Fake IT staff sound calm, use your name and press for speed. A number that looks internal proves nothing, because caller ID can be faked. Hang up and call IT back on the official number.",
      },
      {
        h: "An update sent by email",
        body: "Real IT does not send programs as attachments or ask you to save and run them. An unexpected .exe, even from a familiar name, is a red flag. Report it; don\u2019t open it.",
      },
      {
        h: "One double-click is enough",
        body: "Running the file lets the malware lock files within minutes, often across shared drives. A security prompt on screen is not a safety check. If you did not start it, stop.",
      },
      {
        h: "Data is stolen before it is locked",
        body: "Attackers copy sensitive data first and threaten to publish it. Restoring from backup gets your files back, but it does not take away the threat of a leak.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Ransomware", body: "Malware that locks your files and demands payment to unlock them." },
      { h: "Double extortion", body: "Stealing a copy of the data first, then locking it, so the victim is pressured twice: pay to unlock and pay to stop a leak." },
      { h: "Voice phishing", body: "A scam by phone call, where the caller pretends to be someone you trust, such as IT." },
      { h: "Executable", body: "A file that runs code when opened, such as .exe. Never run one you didn\u2019t ask for." },
      { h: "Offline backup", body: "A copy of your data kept apart from the network, so ransomware can\u2019t encrypt or delete it." },
      { h: "Incident report", body: "A quick message to security when something looks wrong. Early reports limit the damage. Being wrong costs nothing." },
    ],
  },
];
